import { useEffect, useState } from 'react';
import { logoVariantURL, publicURL, type MediaSize } from '@/api/media';

// LogoImg renders an org/team logo from its bare object_key, preferring the
// resize-worker WebP variant for the given size and falling back to the
// full-resolution original on load error. Logos uploaded through the app
// get S/M/L variants (~30s after upload, via internal/services/media/
// resize.go); seeded or legacy logos have none, so the onError swap keeps
// them rendering at original resolution with no regression.
//
// `size` is the largest surface this instance renders at: 'small' (320px)
// covers the sidebar avatar and the settings preview; bump to 'medium' /
// 'large' only for genuinely larger displays.
export function LogoImg({
  objectKey,
  size,
  alt = '',
  className,
}: {
  objectKey: string;
  size: MediaSize;
  alt?: string;
  className?: string | undefined;
}) {
  const original = publicURL(objectKey);
  const preferred = logoVariantURL(objectKey, size) || original;
  const [failed, setFailed] = useState(false);

  // Reset the fallback whenever the source changes (e.g. the user uploads a
  // new logo) so we re-attempt the variant instead of sticking on a stale
  // original.
  useEffect(() => {
    setFailed(false);
  }, [preferred]);

  return (
    <img
      src={failed ? original : preferred}
      alt={alt}
      className={className}
      onError={() => {
        if (preferred !== original) setFailed(true);
      }}
    />
  );
}
